import { useCallback, useEffect, useRef } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { useAuth } from '@/context/AuthContext';
import { useEnv } from '@/context/EnvContext';
import { useLibraryStore } from '@/store/libraryStore';
import { useSettingsStore } from '@/store/settingsStore';
import { isTauriAppPlatform } from '@/services/environment';
import { ingestFile } from '@/services/ingestService';
import { convertToEpubWithWorker } from '@/services/send/conversion/conversionWorker';
import { clipPageWithSignInFallback, isClipCancelled } from '@/services/send/clipSignIn';
import type { ConvertedBook } from '@/services/send/conversion/types';
import { eventDispatcher } from '@/utils/event';
import { createTextBookFile } from '@/utils/txt';
import { parseAnnotationDeepLink, parseDeviceLinkDeepLink } from '@/utils/deeplink';
import { parseShareDeepLink } from '@/utils/share';
import { useTranslation } from './useTranslation';

interface ClipOptions {
  groupId?: string;
  groupName?: string;
  htmlFile?: string;
}

interface PendingShareSave {
  url: string;
  groupId?: string | null;
  groupName?: string | null;
  htmlFile?: string | null;
  /** Shared text (not a URL) saved as a file in App Group `SharedClips/`. */
  textFile?: string | null;
  title?: string | null;
  addedAt?: string;
}

/** Read + delete a file the Share Extension wrote to App Group `SharedClips/`. */
async function readSharedClip(fileName: string): Promise<string | null> {
  const { html } = await invoke<{ html?: string | null }>(
    'plugin:native-bridge|read_share_clip_html',
    { payload: { fileName } },
  );
  return html || null;
}

/**
 * Convert page HTML the iOS Share Extension captured from the user's
 * signed-in Safari tab (App Group file, referenced by `htmlFile`). The
 * native command reads and deletes the file, so this is single-shot.
 */
async function convertSharedHtml(url: string, htmlFile: string): Promise<ConvertedBook> {
  const html = await readSharedClip(htmlFile);
  if (!html) {
    throw new Error('Shared page HTML unavailable');
  }
  return await convertToEpubWithWorker({ kind: 'page', html, url });
}

/**
 * Handle "Share to Minerva" article URLs from the OS share sheet
 * (Safari, Chrome, etc.). Two paths feed in:
 *
 *   1. Deep-link wake-up (`app-incoming-url` event published by
 *      `useAppUrlIngress`) — filters URLs that look like article links
 *      and runs them through the clip → EPUB → import pipeline.
 *
 *   2. iOS Share-Extension App Group queue — the extension writes
 *      `{url, groupId?, groupName?, htmlFile?}` payloads into the shared
 *      NSUserDefaults at `group.com.minerva.app`, and the host
 *      plugin (NativeBridgePlugin) drains them on foreground by calling
 *      `window.__minervaOnShareExtensionPending(saves)`. Same ingest
 *      pipeline, but the chosen library group is preserved. When the
 *      share came from Safari, `htmlFile` points at the page DOM the
 *      extension captured from the signed-in tab (App Group container);
 *      it converts directly, skipping the `clip_url` re-fetch. Shared text
 *      that isn't a URL arrives as `textFile` and imports as a TXT book.
 *
 * On iOS we also expose `window.__minervaGetGroups()` so the
 * Share-Extension picker can show up-to-date library groups, and we
 * post a `{type:'ready'}` to the WKScriptMessageHandler the plugin
 * registered, so the cold-start drain happens even when the extension
 * woke the app up before this hook had mounted.
 *
 * Filter rules — only act on URLs that are:
 *   - http(s) (not file://, content://, minerva://, blob:, data:)
 *   - NOT an annotation deep link (those go to useOpenLaunchLinks)
 *   - NOT a reader sign-in link (those go to useOpenDeviceLink)
 *
 * Failures surface as toasts. Successful clips show "Saving article…"
 * then "Saved to your library." once `ingestFile` completes.
 */
export function useClipUrlIngress() {
  const _ = useTranslation();
  const { envConfig, appService } = useEnv();
  const { user } = useAuth();
  const inflight = useRef<Set<string>>(new Set());

  const showShareError = useCallback(
    (err: unknown) => {
      const detail =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : _('Could not fetch this page');
      eventDispatcher.dispatch('toast', { type: 'error', message: detail, timeout: 3500 });
    },
    [_],
  );

  // Every share-sheet import ends here: ingest, keep the picked group, toast.
  const saveSharedBook = useCallback(
    async (file: File, fallbackTitle: string, options: ClipOptions) => {
      if (!appService) return;
      const { library } = useLibraryStore.getState();
      const { settings } = useSettingsStore.getState();
      const ingested = await ingestFile(
        { file, books: library, forceUpload: true },
        { appService, settings, isLoggedIn: !!user },
      );
      if (!ingested) {
        throw new Error('Import produced no book');
      }
      if (options.groupId) ingested.groupId = options.groupId;
      if (options.groupName) ingested.groupName = options.groupName;
      if (options.groupId || options.groupName) {
        // Group membership merges on its own clock (#5911).
        ingested.updatedAt = Date.now();
        ingested.groupUpdatedAt = ingested.updatedAt;
      }
      await useLibraryStore.getState().updateBooks(envConfig, [ingested]);
      eventDispatcher.dispatch('toast', {
        type: 'success',
        message: _('Saved “{{title}}” to your library.', {
          title: ingested.title || fallbackTitle,
        }),
        timeout: 3000,
      });
    },
    [_, appService, envConfig, user],
  );

  const clipAndImport = useCallback(
    async (url: string, options: ClipOptions = {}) => {
      if (!appService) return;
      if (inflight.current.has(url)) return;
      inflight.current.add(url);

      eventDispatcher.dispatch('toast', {
        type: 'info',
        message: _('Saving article from share…'),
        timeout: 2500,
      });

      try {
        let book: ConvertedBook | null = null;
        // Prefer the DOM the Share Extension already captured from the
        // user's signed-in browser tab — no re-fetch, no login wall.
        if (options.htmlFile) {
          try {
            book = await convertSharedHtml(url, options.htmlFile);
          } catch (err) {
            console.warn('[clip] shared HTML conversion failed, refetching via clip_url', err);
          }
        }
        if (!book) {
          book = await clipPageWithSignInFallback(url, _, appService);
        }
        await saveSharedBook(book.file, book.title || url, options);
      } catch (err) {
        // The user closed the interactive sign-in capture — their call,
        // not an error worth toasting about.
        if (!isClipCancelled(err)) showShareError(err);
      } finally {
        inflight.current.delete(url);
      }
    },
    [_, appService, saveSharedBook, showShareError],
  );

  const importSharedText = useCallback(
    async (textFile: string, title: string, options: ClipOptions) => {
      try {
        const text = await readSharedClip(textFile);
        if (!text?.trim()) {
          throw new Error(_('The shared text is no longer available'));
        }
        const fallbackTitle = _('Shared Text');
        await saveSharedBook(
          createTextBookFile(text, title, fallbackTitle),
          fallbackTitle,
          options,
        );
      } catch (err) {
        showShareError(err);
      }
    },
    [_, saveSharedBook, showShareError],
  );

  // Deep-link path (existing).
  useEffect(() => {
    if (!isTauriAppPlatform() || !appService) return;

    const handle = (url: string) => {
      // iOS Share Extension forwards URLs to the main app in one of
      // two shapes — both are unwrapped to the inner article URL so
      // we can share the http(s) clip path with the Android side:
      //
      //   - Universal Link (primary):
      //       https://web.minerva.invalid/clip?url=<encoded>
      //   - Custom URL scheme (fallback):
      //       minerva://clip?url=<encoded>
      const isClipUrl =
        url.startsWith('minerva://clip?') ||
        url.startsWith('minerva://clip/') ||
        /^https:\/\/web\.minerva\.com\/clip(?:[/?].*)?$/i.test(url);
      if (isClipUrl) {
        try {
          const inner = new URL(url).searchParams.get('url');
          if (inner) {
            url = inner;
          } else {
            return;
          }
        } catch {
          return;
        }
      }
      // Only act on http(s). file://, content://, blob: and data: belong
      // to other consumers (or aren't shareable URLs).
      if (!/^https?:\/\//i.test(url)) return;
      // Annotation deep links can come over https (web.minerva.invalid).
      // Skip them — useOpenLaunchLinks owns that path.
      if (parseAnnotationDeepLink(url)) return;
      // Share links (https://web.minerva.invalid/s/{token}) also arrive over https.
      // Skip them — useOpenShareLink owns that path. Without this guard the
      // share landing URL is run through the article clipper instead of
      // importing the shared book.
      if (parseShareDeepLink(url)) return;
      // Reader sign-in links (https://web.minerva.invalid/link?code=…) open the
      // /link page; useOpenDeviceLink owns that path.
      if (parseDeviceLinkDeepLink(url)) return;
      void clipAndImport(url);
    };

    const onIncoming = (event: CustomEvent) => {
      const { urls } = event.detail as { urls: string[] };
      urls.forEach(handle);
    };
    eventDispatcher.on('app-incoming-url', onIncoming);

    return () => {
      eventDispatcher.off('app-incoming-url', onIncoming);
    };
  }, [appService, clipAndImport]);

  // iOS Share-Extension App Group bridge.
  useEffect(() => {
    if (!isTauriAppPlatform() || !appService) return;
    if (typeof window === 'undefined') return;

    const w = window as unknown as {
      __minervaGetGroups?: () => {
        groups: { id: string; name: string }[];
        defaultGroupName: string;
      };
      __minervaOnShareExtensionPending?: (saves: PendingShareSave[]) => boolean;
      webkit?: {
        messageHandlers?: {
          minervaShareBridge?: { postMessage: (msg: { type: string }) => void };
        };
      };
    };

    // The Share Extension is a native UIKit module and can't read the web
    // app's i18next catalogue. We pass it the user-locale-translated
    // string for "Default" (the no-group entry) alongside the groups —
    // saves maintaining a parallel iOS .strings file just for one phrase.
    w.__minervaGetGroups = () => {
      try {
        return {
          groups: useLibraryStore.getState().getGroups(),
          defaultGroupName: _('Default'),
        };
      } catch {
        return { groups: [], defaultGroupName: 'Default' };
      }
    };

    w.__minervaOnShareExtensionPending = (saves) => {
      if (!Array.isArray(saves)) return false;
      for (const save of saves) {
        if (!save || typeof save.url !== 'string') continue;
        const options = {
          groupId: save.groupId ?? undefined,
          groupName: save.groupName ?? undefined,
        };
        if (save.textFile) {
          void importSharedText(save.textFile, save.title ?? '', options);
          continue;
        }
        void clipAndImport(save.url, { ...options, htmlFile: save.htmlFile ?? undefined });
      }
      return true;
    };

    // Tell the plugin we're mounted so it can drain any pending saves
    // queued before the hook was alive (cold-start path).
    try {
      w.webkit?.messageHandlers?.minervaShareBridge?.postMessage({ type: 'ready' });
    } catch {
      // Non-iOS or handler not registered — no-op.
    }

    return () => {
      const cleanup = window as unknown as {
        __minervaGetGroups?: unknown;
        __minervaOnShareExtensionPending?: unknown;
      };
      delete cleanup.__minervaGetGroups;
      delete cleanup.__minervaOnShareExtensionPending;
    };
  }, [appService, clipAndImport, importSharedText]);
}
